import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { AppShell } from '../components/layout/AppShell'
import { ErrorState, RouteLoading } from '../components/ui/states'
import { useMe, useNotaryMe } from '../lib/queries'
import { useAuth } from './AuthContext'

/**
 * Signed-in routes. Renders the app shell exactly once, so `#main` and the
 * skip link are guaranteed on every authenticated page.
 */
export function RequireAuth() {
  const { isLoading, isAuthenticated } = useAuth()
  const location = useLocation()
  const me = useMe({ enabled: isAuthenticated })

  if (isLoading) return <RouteLoading label="Signing you in" />

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ returnTo: location.pathname + location.search }} />
  }

  if (me.isLoading) {
    return (
      <AppShell>
        <RouteLoading />
      </AppShell>
    )
  }

  if (me.isError) {
    if (me.error.status === 401) {
      return (
        <Navigate to="/login" replace state={{ returnTo: location.pathname + location.search }} />
      )
    }
    return (
      <AppShell>
        <div className="container container--prose">
          <ErrorState error={me.error} onRetry={() => void me.refetch()} />
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  )
}

/**
 * Admin gating is a **UI** decision only: it stops a citizen wandering into a
 * screen that would only 403 anyway. Every admin endpoint re-checks the role
 * server-side, so nothing here is a security boundary.
 *
 * A citizen who lands on /admin/* is sent to /403 and told why — silently
 * bouncing someone to the home page is more confusing than telling them.
 */
export function RequireAdmin() {
  const me = useMe()

  if (me.isLoading) return <RouteLoading />
  if (me.data && !me.data.is_admin) {
    return <Navigate to="/403" replace state={{ area: 'admin' }} />
  }
  return <Outlet />
}

/**
 * The notary area, and its own shell.
 *
 * Deliberately *not* nested inside `RequireAuth`. That guard calls `GET /me`,
 * which provisions a `users` row for whoever is calling — and a notary is not
 * a citizen of this service. Signing one in through the citizen guard would
 * quietly create a second identity for the same person in the table that owns
 * living wills. So this branch authenticates against `GET /notary/me` instead,
 * which resolves the panel row and creates nothing.
 *
 * As with `RequireAdmin`, this is a **UI** decision only. Every `/notary/*`
 * endpoint re-checks the role server-side.
 */
export function RequireNotary() {
  const { isLoading, isAuthenticated } = useAuth()
  const location = useLocation()
  const notary = useNotaryMe({ enabled: isAuthenticated })

  if (isLoading) return <RouteLoading label="Signing you in" />

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ returnTo: location.pathname + location.search }} />
  }

  if (notary.isLoading) {
    return (
      <AppShell audience="notary">
        <RouteLoading />
      </AppShell>
    )
  }

  if (notary.isError) {
    if (notary.error.status === 401) {
      return (
        <Navigate to="/login" replace state={{ returnTo: location.pathname + location.search }} />
      )
    }
    // 403 is the ordinary case here: a citizen or an administrator who typed
    // /notary. Send them somewhere that explains itself rather than looping.
    if (notary.error.status === 403) {
      return <Navigate to="/403" replace state={{ area: 'notary' }} />
    }
    return (
      <AppShell audience="notary">
        <div className="container container--prose">
          <ErrorState error={notary.error} onRetry={() => void notary.refetch()} />
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell audience="notary">
      <Outlet />
    </AppShell>
  )
}
